import React, {useState} from 'react'
import {ShopperBaskets} from 'commerce-api'
import {observer} from 'mobx-react'
import classNames from 'classnames'
import {SubmitHandler, useForm, UseFormReturn} from 'react-hook-form'
import {Box, Flex, Stack, Text, Button} from '../../../vanilla'
import {PromoCodeFields} from '../../../components/forms'
import {actionBoxContainer, addPromocodeActionContainer} from './styles.css'
import {useBasketStore, useGlobalStore} from '../../../store/hooks/useStore'
import {Nullable} from '../../../types/utils'
import {flowResult} from 'mobx'
import {ExclamationCircleIcon, TrashIcon} from '../../../components/icons'
import {FormattedNumber} from 'react-intl'

interface PromocodeFormValues {
  code: string
}

export const AddPromocodeContainer = () => {
  const {currency} = useGlobalStore()
  const {setPromoCode, removePromoCode, appliedPromocode, basket} = useBasketStore()
  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null)

  const promocodeForm = useForm<PromocodeFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      code: '',
    },
  })

  const handleSubmit: SubmitHandler<PromocodeFormValues> = async ({code}) => {
    setErrorMessage(null)
    const result = await flowResult(setPromoCode(code))

    if (result.error) {
      setErrorMessage(result.message)
    }
  }

  const handleRemovePromocode = async () => {
    if (!appliedPromocode || !appliedPromocode.couponItemId) return

    const result = await flowResult(removePromoCode(appliedPromocode.couponItemId))
    
    if (!result.error) {
      promocodeForm.reset()
    }
  }

  const couponBasketPriceAdjustment = appliedPromocode
    ? basket?.orderPriceAdjustments?.find(
        (adjustment) => adjustment.couponCode === appliedPromocode.code,
      ) || null
    : null

  return (
    <Box
      className={classNames(actionBoxContainer, addPromocodeActionContainer)}
      paddingX="16px"
      paddingY="20px"
      marginTop="12px"
      borderTop={['1px', '0px']}
      borderColor="gray200"
      bg="white"
    >
      {!appliedPromocode ? (
        <AddPromocodeEmpty form={promocodeForm} onSubmit={handleSubmit} error={errorMessage} />
      ) : (
        <AppliedPromocode
          currency={currency}
          priceAdjustment={couponBasketPriceAdjustment}
          onRemove={handleRemovePromocode}
          couponItem={appliedPromocode}
        />
      )}
    </Box>
  )
}

export const AddPromocodeAction = observer(AddPromocodeContainer)

interface AddPromocodeEmptyProps {
  onSubmit: SubmitHandler<PromocodeFormValues>
  form: UseFormReturn<PromocodeFormValues>
  error: Nullable<string>
}

const AddPromocodeEmpty = ({onSubmit, form, error}: AddPromocodeEmptyProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true)

  return (
    <>
      {isCollapsed ? (
        <Box data-test-selector="add-promo-code-link" onClick={() => setIsCollapsed(false)}>
          <Text variant="text4" cursor="pointer" textDecoration="underline" color="gray800">
            Add a promo code
          </Text>
        </Box>
      ) : (
        <Stack as="form" onSubmit={form.handleSubmit(onSubmit)}>
          <PromoCodeFields form={form} display="flex" justifyContent="space-between" />
          {error && (
            <Text
              as="span"
              variant="text4"
              color="error"
              display="inline-block"
              marginLeft="8px"
              lineHeight="shorter"
            >
              <ExclamationCircleIcon
                color="error"
                display="inline-block"
                boxSize="12px"
                style={{marginRight: '6.5px'}}
              />
              {error}
            </Text>
          )}
        </Stack>
      )}
    </>
  )
}

interface AppliedPromocodeProps {
  onRemove: () => void
  couponItem: ShopperBaskets.CouponItem
  priceAdjustment: Nullable<ShopperBaskets.PriceAdjustment>
  currency: string
}

const AppliedPromocode = ({
  couponItem,
  onRemove,
  priceAdjustment,
  currency,
}: AppliedPromocodeProps) => {
  return (
    <Flex direction={['column', 'row']} gap="8px">
      <Box width={['full', 'two-thirds']} flexShrink="0">
        <Text variant="text4" color="accent1" lineHeight="shorter">
          Congratulations! You have {couponItem.promotionCalloutMsg} on your basket.
        </Text>
      </Box>
      <Flex
        gap="16px"
        align="center"
        flexGrow="1"
        justifyContent={['space-between', 'flex-end']}
        width={['full', 'auto']}
      >
        {priceAdjustment && (
          <Stack spacing="0px">
            {priceAdjustment.price != null && (
              <Text variant="text7" lineHeight="shorter" align={['left', 'right']}>
                {currency && (
                  <FormattedNumber
                    value={priceAdjustment.price}
                    currency={currency}
                    style="currency"
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                  />
                )}
              </Text>
            )}
            {priceAdjustment.couponCode && (
              <Text
                variant="text1"
                color="textMuted"
                lineHeight="shorter"
                align={['left', 'right']}
              >
                {priceAdjustment.couponCode}
              </Text>
            )}
          </Stack>
        )}
        <Flex direction={['column']} gap="8px">
          <Text variant="text5" color="dark1" lineHeight="shorter">
            <FormattedNumber
              value={couponItem.promotionDiscount || 0}
              currency={currency}
              style="currency"
            />
          </Text>
          <Text variant="text4" color="accent2" lineHeight="shorter">
            {couponItem.code}
          </Text>
        </Flex>
        <Button variant="unstyled" onClick={() => onRemove()} paddingX="0px" height="auto">
          <TrashIcon style={{width: 18}} height="auto" color="gray800" />
        </Button>
      </Flex>
    </Flex>
  )
}
